import { useMemo, useState } from 'react';
import { where } from 'firebase/firestore';
import {
  ORG_PERMISSIONS,
  ORG_ROLES,
  canGrantOrgRole,
  canOrg,
  type OrgInvite,
  type OrgMember,
  type OrgPermission,
  type OrgRole,
} from '@allemnii-pulse/shared';
import { api, ApiError } from '../../lib/api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useCollection } from '../../lib/firestore.js';
import { useUserProfiles } from '../../lib/userProfiles.js';
import { UserStatusDot } from '../../components/UserStatus.js';
import { useOrgs } from './OrgProvider.js';
import { DevPreferencesCard } from './DevPreferencesCard.js';
import { TrainingDefaultsCard } from '../training/TrainingInstructionsCard.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';

const ROLE_LABEL: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  guest: 'Guest',
};

/** Row labels for the RBAC matrix shown under "Invite a teammate". Keyed by every OrgPermission so
 * a new permission can't be added to the shared matrix without a label here. */
const PERMISSION_LABEL: Record<OrgPermission, string> = {
  'org.view': 'See the org and your projects',
  'org.members.view': 'See the member directory',
  'org.project.create': 'Create projects',
  'org.projects.viewAll': 'Open every project in the org',
  'org.update': 'Rename org, edit dev defaults',
  'org.members.manage': 'Invite and remove members & guests',
  'org.admins.manage': 'Promote, demote or remove admins',
  'org.owner.grant': 'Grant or transfer ownership',
  'org.audit.read': 'Read the audit log',
  'org.teamActivity.view': 'View team activity history',
  'org.delete': 'Delete the organisation',
};

function OrgRoleMatrix() {
  const permissions = Object.keys(ORG_PERMISSIONS) as OrgPermission[];
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-border bg-surface-2">
            <th className="px-2 py-1.5 text-left font-medium">Permission</th>
            {ORG_ROLES.map((r) => (
              <th key={r} className="px-2 py-1.5 text-center font-medium">
                {ROLE_LABEL[r]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {permissions.map((p) => (
            <tr key={p} className="border-b border-border last:border-b-0">
              <td className="px-2 py-1.5">{PERMISSION_LABEL[p]}</td>
              {ORG_ROLES.map((r) => (
                <td key={r} className="px-2 py-1.5 text-center">
                  {canOrg(r, p) ? (
                    <span aria-label="Allowed">✓</span>
                  ) : (
                    <span className="text-muted" aria-label="Not allowed">
                      —
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface MemberRow extends OrgMember {
  email?: string;
  displayName?: string;
}

function roleBadgeVariant(role: OrgRole) {
  if (role === 'owner') return 'default';
  if (role === 'admin') return 'secondary';
  return 'outline';
}

/** Org-level settings: who's in the org, and inviting new teammates by email — no domain
 * restriction, since acceptance rides on the normal Google sign-in flow (see POST /auth/session).
 * See docs/PLAN.md §14 item 3. What each control offers follows the RBAC matrix in
 * packages/shared/src/permissions.ts: admins manage members and guests, only an owner manages
 * admins, grants owner, or deletes the org; guests don't see the member directory at all. */
export function OrgSettingsPage() {
  const { currentOrg, currentOrgId, refresh } = useOrgs();
  const { user } = useAuth();
  const myRole = currentOrg?.role;
  const canManage = canOrg(myRole, 'org.members.manage');
  const canViewMembers = canOrg(myRole, 'org.members.view');
  const canDeleteOrg = canOrg(myRole, 'org.delete');
  const invitableRoles = ORG_ROLES.filter((r) => canGrantOrgRole(myRole, null, r));

  const membersPath = currentOrgId && canViewMembers ? `orgs/${currentOrgId}/members` : null;
  const { data: members, loading: membersLoading } = useCollection<OrgMember>(membersPath);
  const profiles = useUserProfiles(members.map((m) => m.uid));
  const [memberError, setMemberError] = useState<string | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleChangeRole(uid: string, role: OrgRole) {
    if (!currentOrgId) return;
    setBusyUid(uid);
    setMemberError(null);
    try {
      await api.patch(`/orgs/${currentOrgId}/members/${uid}`, { role });
    } catch (err) {
      setMemberError(err instanceof ApiError ? err.message : 'Could not change that role.');
    } finally {
      setBusyUid(null);
    }
  }

  async function handleRemove(uid: string) {
    if (!currentOrgId) return;
    setBusyUid(uid);
    setMemberError(null);
    try {
      await api.delete(`/orgs/${currentOrgId}/members/${uid}`);
    } catch (err) {
      setMemberError(err instanceof ApiError ? err.message : 'Could not remove that member.');
    } finally {
      setBusyUid(null);
    }
  }

  async function handleDeleteOrg() {
    if (!currentOrgId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/orgs/${currentOrgId}`);
      setConfirmDeleteOpen(false);
      await refresh();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Could not delete the organisation.');
    } finally {
      setDeleting(false);
    }
  }

  const invitesPath = currentOrgId && canManage ? `orgs/${currentOrgId}/invites` : null;
  const pendingConstraints = useMemo(() => [where('status', '==', 'pending')], []);
  const { data: pendingInvites } = useCollection<OrgInvite>(invitesPath, pendingConstraints);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInvitedEmail, setLastInvitedEmail] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!currentOrgId || !email.trim()) return;
    setInviting(true);
    setInviteError(null);
    try {
      await api.post(`/orgs/${currentOrgId}/invites`, { email: email.trim(), role });
      setLastInvitedEmail(email.trim());
      setEmail('');
    } catch (err) {
      setInviteError(
        err instanceof ApiError ? err.message : 'Could not send the invite. Please try again.',
      );
    } finally {
      setInviting(false);
    }
  }

  async function handleRevoke(inviteId: string) {
    if (!currentOrgId) return;
    setRevokingId(inviteId);
    try {
      await api.delete(`/orgs/${currentOrgId}/invites/${inviteId}`);
    } finally {
      setRevokingId(null);
    }
  }

  const rows: MemberRow[] = members.map((m) => ({ ...m, ...profiles[m.uid] }));

  if (!currentOrg) {
    return <div className="p-4 text-sm text-muted sm:p-6">Loading…</div>;
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Org settings</h1>
        <p className="text-sm text-muted">Manage who's in {currentOrg.name}.</p>
      </div>

      {!canViewMembers ? (
        <Card>
          <CardHeader>
            <CardTitle>Your access</CardTitle>
            <CardDescription>
              You're a guest in {currentOrg.name}: you can see the projects you've been added to,
              but not the organisation's member list or settings.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Members</CardTitle>
            <CardDescription>
              Everyone with access to this organisation. Guests only see the projects they're added
              to. Removing someone also removes them from every project in the org.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {membersLoading && <p className="text-sm text-muted">Loading…</p>}
            {!membersLoading && rows.length === 0 && (
              <p className="text-sm text-muted">No members yet.</p>
            )}
            {rows.map((m) => {
              const isSelf = m.uid === user?.uid;
              const roleOptions = ORG_ROLES.filter((r) => canGrantOrgRole(myRole, m.role, r));
              const canChange = !isSelf && roleOptions.some((r) => r !== m.role);
              const canRemove = !isSelf && canGrantOrgRole(myRole, m.role, null);
              return (
                // Stacked on a phone: the role select and Remove button beside a name and email
                // would leave the name only a few characters wide.
                <div
                  key={m.uid}
                  className="flex flex-col gap-2 py-1 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="flex items-center gap-1.5 text-sm wrap-anywhere">
                      <UserStatusDot uid={m.uid} />
                      {m.displayName ?? m.email ?? m.uid}
                    </span>
                    {m.displayName && m.email && (
                      <span className="pl-3.5 text-xs text-muted wrap-anywhere">{m.email}</span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pl-3.5 sm:pl-0">
                    {canChange ? (
                      <Select
                        value={m.role}
                        disabled={busyUid === m.uid}
                        onValueChange={(v) => void handleChangeRole(m.uid, v as OrgRole)}
                      >
                        <SelectTrigger className="h-10 w-28 text-xs sm:h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from(new Set([m.role, ...roleOptions])).map((r) => (
                            <SelectItem key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={roleBadgeVariant(m.role)}>{m.role}</Badge>
                    )}
                    {canRemove && (
                      <Button
                        type="button"
                        variant="destructive"
                        className="h-10 sm:h-8"
                        disabled={busyUid === m.uid}
                        onClick={() => void handleRemove(m.uid)}
                      >
                        {busyUid === m.uid ? 'Working…' : 'Remove'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {memberError && <p className="text-sm text-danger">{memberError}</p>}
          </CardContent>
        </Card>
      )}

      {currentOrgId && canViewMembers && (
        <>
          <DevPreferencesCard orgId={currentOrgId} canManage={canOrg(myRole, 'org.update')} />
          <TrainingDefaultsCard orgId={currentOrgId} canManage={canOrg(myRole, 'org.update')} />
        </>
      )}

      {canManage && (
        <Card>
          <form onSubmit={(e) => void handleInvite(e)}>
            <CardHeader>
              <CardTitle>Invite a teammate</CardTitle>
              <CardDescription>
                Any email works, even from outside your org's domain — they just need to sign in
                with Google using that exact address.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@example.com"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-role">Role</Label>
                <Select value={role} onValueChange={(v) => setRole(v as OrgRole)}>
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...invitableRoles].reverse().map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted">What each role can do</span>
                <OrgRoleMatrix />
              </div>
              {inviteError && <p className="text-sm text-danger">{inviteError}</p>}
              {lastInvitedEmail && !inviteError && (
                <div className="rounded-lg border border-border bg-surface-2 p-3 text-sm wrap-anywhere">
                  <p className="mb-1.5">
                    Invited <strong>{lastInvitedEmail}</strong>. Send them this link — they'll get
                    access the moment they sign in with that Google account:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-bg px-2 py-1 text-xs">
                      {window.location.origin}
                    </code>
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-10 shrink-0 sm:h-8"
                      onClick={() => void navigator.clipboard.writeText(window.location.origin)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" variant="default" disabled={inviting || !email.trim()}>
                {inviting ? 'Sending…' : 'Send invite'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Pending invites</CardTitle>
            <CardDescription>Invites that haven't been accepted yet.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {pendingInvites.length === 0 && (
              <p className="text-sm text-muted">No pending invites.</p>
            )}
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between gap-2 py-1">
                <div className="flex min-w-0 flex-col">
                  <span className="text-sm wrap-anywhere">{invite.email}</span>
                  <span className="text-xs text-muted">Invited as {invite.role}</span>
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  className="h-10 shrink-0 sm:h-8"
                  disabled={revokingId === invite.id}
                  onClick={() => void handleRevoke(invite.id)}
                >
                  {revokingId === invite.id ? 'Revoking…' : 'Revoke'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {canDeleteOrg && (
        <Card className="border-danger/40">
          <CardHeader>
            <CardTitle>Delete organisation</CardTitle>
            <CardDescription>
              Only possible once every project in it has been deleted. Removes the organisation, its
              members, invites and settings. The audit log keeps a record that it existed.
            </CardDescription>
          </CardHeader>
          {deleteError && (
            <CardContent>
              <p className="text-sm text-danger">{deleteError}</p>
            </CardContent>
          )}
          <CardFooter className="justify-end">
            <Button variant="destructive" onClick={() => setConfirmDeleteOpen(true)}>
              Delete organisation
            </Button>
          </CardFooter>
        </Card>
      )}

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogHeader>
          <DialogTitle className="pr-6 wrap-break-word">Delete {currentOrg.name}?</DialogTitle>
          <DialogDescription>
            Everyone loses access to this organisation. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-wrap">
          <Button type="button" onClick={() => setConfirmDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={deleting} onClick={() => void handleDeleteOrg()}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
